import { DEFAULT_EXCLUDED_STATUSES, InvoicesDashboard } from '@/app/features/DebtorPortal/Dashboard/dashboard.types';
import {
  formatDate,
  formatDateForSubmission,
  formatMoney,
} from '@/app/shared/utils/tableUtils';
import { RowChanges, UpdateInvoiceCollectionStatusRequest } from './invoiceTypes';

/**
 * Extracts date portion from ISO datetime string or returns as-is if already date-only
 */
const extractDateFromISO = (dateString: string | null | undefined): string | undefined => {
  if (!dateString) return undefined;

  // If it contains 'T', extract the date part
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  return dateString;
};

/**
 * Transforms raw API invoice data into display-ready format
 */
export const transformInvoiceForDisplay = (
  invoice: InvoicesDashboard,
): InvoicesDashboard => ({
  ...invoice,
  willPay: invoice.willPay ?? true,
  invoiceDate: formatDate(invoice.invoiceDate),
  dueDate: invoice.dueDate
    ? formatDate(invoice.dueDate)
    : '-',
  amount: formatMoney(invoice.amount),
  amountDue: formatMoney(invoice.amountDue),
  // Fix: Extract date portion from ISO datetime string
  paymentDate: extractDateFromISO(invoice.paymentDate),
  // Fix: Convert disputedReasonId to string for component compatibility
  disputedReasonId: invoice.disputedReasonId
    ? invoice.disputedReasonId.toString()
    : '',
  // Fix: Map disputedReasonName to reason field
  reason: (invoice as any).disputedReasonName ?? invoice.reason ?? '',
  // Fix: Ensure userText is passed through
  userText: invoice.userText ?? '',
});

/**
 * Transforms an array of raw invoices for display
 */
export const transformInvoicesForDisplay = (
  invoices: InvoicesDashboard[],
): InvoicesDashboard[] => invoices.map(transformInvoiceForDisplay);

/**
 * Checks if an invoice is past due
 */
export const isInvoicePastDue = (dueDate: string | Date | null | undefined): boolean => {
  if (!dueDate) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let dueDateObj: Date;
  if (typeof dueDate === 'string') {
    dueDateObj = new Date(dueDate);
  } else {
    dueDateObj = dueDate;
  }
  dueDateObj.setHours(0, 0, 0, 0);

  return dueDateObj < today;
};

/**
 * Checks if a row has valid response (either payment date OR disputed reason)
 */
export const hasValidResponse = (
  paymentDate: string | null | undefined,
  disputedReasonId: string | number | null | undefined,
): boolean => {
  const hasPaymentDate = paymentDate != null && paymentDate !== '';
  const hasDisputedReason = disputedReasonId != null && disputedReasonId !== '' && disputedReasonId !== 0;

  return hasPaymentDate || hasDisputedReason;
};

/**
 * Checks if a row should show a warning
 */
export const shouldShowWarning = (
  row: InvoicesDashboard,
  changes?: RowChanges,
): boolean => {
  const isPastDue = isInvoicePastDue(row.dueDate);
  const isExcluded = DEFAULT_EXCLUDED_STATUSES.includes(row.status ?? '');
  
  if (!isPastDue || isExcluded) {
    return false;
  }

  let paymentDate: string | null | undefined;
  let disputedReasonId: string | number | null | undefined;

  if (changes) {
    paymentDate = changes.paymentDate !== undefined ? changes.paymentDate : row.paymentDate;
    disputedReasonId = changes.disputedReasonId !== undefined ? changes.disputedReasonId : row.disputedReasonId;
  } else {
    paymentDate = row.paymentDate;
    disputedReasonId = row.disputedReasonId;
  }

  return !hasValidResponse(paymentDate, disputedReasonId);
};

/**
 * Merges raw rows with pending changes to create display rows
 * Also calculates showWarning for each row
 */
export const mergeRowsWithChanges = (
  rawRows: InvoicesDashboard[],
  pendingChanges: Map<number, RowChanges>,
): InvoicesDashboard[] => {
  return rawRows.map((row) => {
    const changes = pendingChanges.get(row.invoiceId);
    const mergedRow = changes
      ? {
          ...row,
          ...changes,
        }
      : row;

    return {
      ...mergedRow,
      showWarning: shouldShowWarning(row, changes),
    };
  });
};

/**
 * Validates all rows and returns validation state (for display purposes)
 */
export const validateRows = (
  rows: InvoicesDashboard[],
): { isValid: boolean; rowsWithWarnings: InvoicesDashboard[]; rowsWithWarningsCount: number } => {
  const rowsWithWarnings = rows.filter((row) => row.showWarning);

  return {
    isValid: rowsWithWarnings.length === 0,
    rowsWithWarnings,
    rowsWithWarningsCount: rowsWithWarnings.length,
  };
};

/**
 * Validates only modified rows for submission
 * Returns validation state for rows that have pending changes
 */
export const validateModifiedRows = (
  modifiedRows: InvoicesDashboard[],
  rawRows: InvoicesDashboard[],
): { isValid: boolean; invalidRows: InvoicesDashboard[]; invalidRowsCount: number } => {
  const invalidRows: InvoicesDashboard[] = [];

  modifiedRows.forEach((modifiedRow) => {
    const originalRow = rawRows.find((r) => r.invoiceId === modifiedRow.invoiceId);
    const isPastDue = isInvoicePastDue(originalRow?.dueDate || modifiedRow.dueDate);

    if (isPastDue) {
      const hasResponse = hasValidResponse(modifiedRow.paymentDate, modifiedRow.disputedReasonId);
      if (!hasResponse) {
        invalidRows.push(modifiedRow);
      }
    }
  });

  return {
    isValid: invalidRows.length === 0,
    invalidRows,
    invalidRowsCount: invalidRows.length,
  };
};

/**
 * Builds submission payload from modified rows
 */
export const buildSubmitPayload = (
  modifiedRows: InvoicesDashboard[],
): UpdateInvoiceCollectionStatusRequest[] => {
  return modifiedRows.map((row) => {
    const payload: UpdateInvoiceCollectionStatusRequest = {
      invoiceId: typeof row.invoiceId === 'string'
        ? parseInt(row.invoiceId, 10)
        : row.invoiceId,
      willPay: row.willPay ?? false,
      urgent: row.urgent ?? false,
      userText: row.userText ?? '',
    };

    if (row.paymentDate) {
      payload.paymentDate = row.paymentDate;
    }

    if (row.disputedReasonId) {
      payload.disputedReasonId = typeof row.disputedReasonId === 'string'
        ? parseInt(row.disputedReasonId, 10)
        : row.disputedReasonId;
    }

    return payload;
  });
};

/**
 * Checks if a row change has meaningful data that differs from original
 */
export const hasSubmittableChanges = (
  changes: RowChanges,
  originalRow?: InvoicesDashboard
): boolean => {
  // If no original row, check if changes have values
  if (!originalRow) {
    const hasPaymentDate =
      changes.paymentDate !== undefined &&
      changes.paymentDate !== null &&
      changes.paymentDate !== '';
    const hasDisputedReason =
      changes.disputedReasonId !== undefined &&
      changes.disputedReasonId !== null &&
      changes.disputedReasonId !== '';

    return hasPaymentDate || hasDisputedReason;
  }

  // Compare changes to original values
  let hasDifferentPaymentDate = false;
  let hasDifferentDisputedReason = false;
  let hasDifferentUserText = false;

  if (changes.paymentDate !== undefined) {
    const originalPaymentDate = originalRow.paymentDate ?? '';
    hasDifferentPaymentDate = changes.paymentDate !== originalPaymentDate;
  }

  if (changes.disputedReasonId !== undefined) {
    const originalDisputedReasonId = originalRow.disputedReasonId?.toString() ?? '';
    hasDifferentDisputedReason = changes.disputedReasonId !== originalDisputedReasonId;
  }

  if (changes.userText !== undefined) {
    const originalUserText = originalRow.userText ?? '';
    hasDifferentUserText = changes.userText !== originalUserText;
  }

  return hasDifferentPaymentDate || hasDifferentDisputedReason || hasDifferentUserText;
};

/**
 * Extracts invoice IDs that have submittable pending changes
 */
export const getInvoiceIdsWithChanges = (
  pendingChanges: Map<number, RowChanges>,
  rawRows?: InvoicesDashboard[],
): Set<number> => {
  const ids = new Set<number>();
  pendingChanges.forEach((changes, invoiceId) => {
    const originalRow = rawRows?.find(r => r.invoiceId === invoiceId);
    if (hasSubmittableChanges(changes, originalRow)) {
      ids.add(invoiceId);
    }
  });
  return ids;
};

/**
 * Gets all modified rows from pending changes
 */
export const getModifiedRows = (
  pendingChanges: Map<number, RowChanges>,
  rawRows: InvoicesDashboard[],
): InvoicesDashboard[] => {
  const modifiedList: InvoicesDashboard[] = [];

  pendingChanges.forEach((changes, invoiceId) => {
    const currentRow = rawRows.find((r) => r.invoiceId === invoiceId);
    if (currentRow && hasSubmittableChanges(changes, currentRow)) {
      modifiedList.push({
        ...currentRow,
        ...changes,
      });
    }
  });

  return modifiedList;
};

/**
 * Gets invoice IDs that have warnings (need action)
 */
export const getInvoiceIdsWithWarnings = (
  rows: InvoicesDashboard[],
): Set<number> => {
  const ids = new Set<number>();
  rows.forEach((row) => {
    if (row.showWarning) {
      ids.add(row.invoiceId);
    }
  });
  return ids;
};

/**
 * Gets all past due invoices from a list of rows
 */
export const getPastDueInvoices = (
  rows: InvoicesDashboard[],
): InvoicesDashboard[] => {
  return rows.filter((row) => isInvoicePastDue(row.dueDate));
};

/**
 * Counts past due invoices
 */
export const countPastDueInvoices = (
  rows: InvoicesDashboard[],
): number => {
  return rows.filter((row) => isInvoicePastDue(row.dueDate)).length;
};

/**
 * Build payload for bulk payment date updates
 */
export const buildBulkPaymentPayload = (
  invoiceRows: InvoicesDashboard[],
  selectedPaymentDate: string,
  note: string = ''
): any[] => {
  const formattedDate = formatDateForSubmission(selectedPaymentDate);

  return invoiceRows.map((row) => ({
    invoiceId: row.invoiceId,
    paymentDate: formattedDate,
    willPay: true,
    disputedReasonId: null,
    reason: null,
    userText: note,
  }));
};

/**
 * Build payload for bulk will not pay updates
 */
export const buildBulkWillNotPayPayload = (
  invoiceRows: InvoicesDashboard[],
  disputedReasonId: string,
  disputedReasonDesc: string,
  note: string
): any[] => {
  return invoiceRows.map((row) => ({
    invoiceId: row.invoiceId,
    paymentDate: null,
    willPay: false,
    disputedReasonId: disputedReasonId,
    reason: disputedReasonDesc,
    userText: note,
  }));
};
